import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const restorationServices = [
  "Full body restoration",
  "Rust repair and panel replacement",
  "Engine bay detailing",
  "Chrome restoration",
  "Interior restoration",
  "Mechanical refurbishment",
  "Electrical systems restoration",
  "Concours-quality finishing"
];

const RestorationServicesPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Classic Car Restoration',
    'Expert vintage and classic car restoration services including full body restoration, rust repair, chrome restoration, and concours-quality finishing in Murray Bridge, South Australia.',
    'https://www.jprcustoms.com.au/restoration-services'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Vintage & Classic Car Restoration Services"
        description="Expert classic car restoration in Murray Bridge SA. Full body restoration, rust repair, panel replacement, chrome restoration, interior restoration & concours-quality finishing. Call +61 417 119 544."
        keywords="classic car restoration, vintage car restoration, car restoration Murray Bridge, antique car restoration, rust repair, chrome restoration, classic car respray"
        canonical="/restoration-services"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1591278169757-deac26e49555?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Vintage and Classic<br />
              <span className="text-lime-500">Restoration Services</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Breathe new life into your classic vehicle with our meticulous restoration services.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-lime-500 mb-6">Expert Classic Car Restoration</h2>
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  At JPR Customs, we understand that your classic car is more than just a vehicle – it's a piece of automotive history. Our team of skilled craftsmen brings decades of experience to every restoration project.
                </p>
                <p className={`mb-8 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Whether you're looking for a complete frame-off restoration or specific repairs, we have the expertise and facilities to deliver concours-quality results.
                </p>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Our Restoration Services Include:</h3>
                <ul className="space-y-3">
                  {restorationServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <img 
                  src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800&q=80" 
                  alt="Classic car restoration"
                  className="w-full h-64 object-cover rounded-xl"
                />
                <img 
                  src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80" 
                  alt="Restored classic car"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Ready to Restore Your Classic?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Contact us today for a free consultation and quote.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RestorationServicesPage;
